import _ from 'lodash';

import { BannerImage, BannerInterface } from '@/interfaces/BannerInterface';
import { OverviewMenuItem } from '@/interfaces/OverviewMenuItem';
import { Tab } from '@/interfaces/Tab';
import i18n from '@/plugins/i18n/i18n';
import notification from '@/services/notification';
import { constants } from '@/shared/constants';
import { urls } from '@/shared/urls';
import { VueTranslateResult } from '@/types';

export function urlWithParams(url: string, params: { [index: string]: any }) {
	let result = url;
	Object.keys(params).forEach(param => {
		const value = param === 'token' ? encodeURIComponent(params[param]) : params[param];
		result = result.replace(`{${ param }}`, value);
	});
	return result;
}

export function range(size: number, startAt = 0): number[] {
	return [ ...Array(size).keys() ].map(i => i + startAt);
}

export function normalizePhone(phone: string) {
	const result = phone.replace(/[^0-9]/g, '');
	return result.startsWith('0') ? result.replace('0', '387') : result;
}

export function encodePassword(password: string): string {
	return `<b64>${ btoa(password) }</b64>`;
}

export function handleError(callback: Function = () => {}) {
	return (data: {response:{ data: {errors: {type: string, statusCode: string, message?: string}[]}}, handled?: boolean}) => {
		callback(data.response);
		if (data.handled)
			return;

		// catch error message from BE
		if (data.response.data?.errors?.[0]?.message) {
			notification.error(data.response.data.errors[0].message);
			return;
		}

		const tPrefix = 'message.common.notification.error';
		notification.error(i18n.t(`${ tPrefix }.message`), i18n.t(`${ tPrefix }.description`));
	};
}

export function getUrlParams() {
	const obj: { [index: string]: string } = {};
	new URLSearchParams(location.search.slice(1)).forEach((value, key) => obj[key] = value);
	return obj;
}

export function clearUrlParams(withSearch: boolean = false) {
	window.history.pushState({}, document.title, location.pathname + (withSearch ? location.search : ''));
}

export function forbidScroll(flag: boolean) {
	const scrollbarWidth = flag ? window.innerWidth - document.body.offsetWidth : 0;
	(document.querySelector('body') as HTMLBodyElement).style.paddingRight = `${ scrollbarWidth }px`;
	document.body.classList[flag ? 'add' : 'remove']('hide-overflow');
}

export function isIE() {
	return /msie\s|trident\//i.test(window.navigator.userAgent);
}

export function isMobileAndTablet() {
	let check = false;
	(function(a: any) {
		if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4)))
			check = true;
	})(navigator.userAgent || navigator.vendor);
	return check;
}

export function isProfile() {
	return constants.profilePages.some((page: string) => urls[page].toLowerCase() === location.pathname.toLowerCase());
}

export function getUrlOrSubscriber(data: OverviewMenuItem[]): string {
	// for local testing
	if (~location.origin.indexOf('localhost'))
		data = JSON.parse(JSON.stringify(data).replaceAll(constants.devEnvUrl, 'https://localhost:8080'));

	let customerWithServices = data?.find((customer: any) => customer?.services?.[0]?.parts?.[0]?.url);
	return customerWithServices.services?.[0]?.parts?.[0]?.url;
}

export function normalizeTariffOptionsData(data: any): any {
	return _
		.chain(data.products)
		.map((product: any) => product.categories)
		.flatten()
		.map((category: any) => _.omit(category, ['optionOrder']))
		.uniqBy('code')
		.sortBy('order')
		.each((category: any) => {
			category.products = [];
			category.products = _
				.chain(data.products)
				.map((product: any) => {
					const clonedProduct = _.cloneDeep(product);
					const foundCategory = _.filter(clonedProduct.categories, { code: category.code })?.[0];
					if (foundCategory) {
						clonedProduct.optionOrder = foundCategory.optionOrder;
						return clonedProduct;
					}
				})
				.without(undefined)
				.sortBy('optionOrder')
				.value();
		})
		.value();
}

export function setContentPadding() {
	const headerHeight = document.querySelector('header')?.getBoundingClientRect().height;
	const contentElement = document.querySelector('.main-layout__content-container') as HTMLElement;
	contentElement.style.paddingTop = `${ headerHeight }px`;
}

export function menuHeight() {
	const sidemenuWrapper = document.querySelector('.sub-nav-menu__desktop') as HTMLElement;
	const footer = document.querySelector('.footer-container') as HTMLElement;
	const menu = document.querySelector('.sub-menu') as HTMLElement;

	if(!menu)
		return;

	menu.style.height = 'auto';
	if(sidemenuWrapper?.getBoundingClientRect().bottom >= footer?.getBoundingClientRect().top)
		menu.style.height = `${footer?.getBoundingClientRect().top - 200}px`;
}

export function groupBy<T>(list: Array<T>, keyGetter: Function) {
	const map = new Map();

	list.forEach((item: T) => {
		const key = keyGetter(item);
		const collection = map.get(key);

		collection
			? collection.push(item)
			: map.set(key, [item]);
	});

	return map;
}

export function isEmpty(obj: any): boolean {
	if (!obj) return true;

	return !Object.keys(obj).length;
}

export function getImageByBreakpoint(banner: BannerInterface, windowWidth: number): string {
	if (isEmpty(banner)) return '';

	const suitableImages = banner.images
		.filter((image: BannerImage) => windowWidth >= image.resolution)
		.sort((a, b) => (a.resolution - b.resolution));
	const nearestImage = suitableImages[suitableImages.length - 1];
	const img = new Image();

	img.src = nearestImage.url;

	return nearestImage.url;
}

export function addLocalRouteProp(props: any) {
	return {
		...( location.hostname === 'localhost' && { props: { ...props } }),
	};
}

export function createTabs(names: VueTranslateResult, urls?: string[]): Tab[] {
	return [].slice.call(names).map((name: VueTranslateResult, i: number) => ({ name, state: false, url: urls?.[i] }));
}
